/**
 * Which documents the group data-import dialog accepts, and how it reads a
 * preflight preview.
 *
 * The preflight answers what an uploaded document would produce without
 * writing anything; the dialog has to decide from that answer whether the
 * import is worth committing and which of the reader's warnings to show. These
 * are the rules behind that screen, kept apart from the rendering so they can
 * be stated once and tested.
 *
 * A document also arrives under whatever headings the person who built it
 * chose, so the reader resolves the ones it recognizes and the submitter points
 * the rest at a field by hand. The mapping is keyed by heading in both
 * directions, and these are the rules that seed that choice, send it, and
 * decide when the preview beside it has gone stale.
 *
 * Contains:
 * - GROUP_IMPORT_ACCEPT — the document formats the dropzone admits
 * - WARNING_DISPLAY_LIMIT — how many warnings a preview renders in full
 * - IGNORED_FIELD — the picker value standing for "this column is not imported"
 * - SITE_SCOPE / LOAD_SCOPE — the two things a document's columns describe
 * - summarizeImportPreview — a preview's counts in the dialog's vocabulary
 * - canCommitPreview — whether a preview may be submitted
 * - selectWarningsToShow — the warnings to render, and how many are left over
 * - warningRowKey — a stable, collision-free React key for a warning row
 * - normalizeImportHeader — the comparison form a heading is matched under
 * - groupCatalogByScope — the catalog split into the picker's option groups
 * - buildColumnMappings — the picker model, seeded from a preview
 * - applyColumnMapping — point one column at a field, or at nothing
 * - toFieldMapPayload — the picker model as the mapping the endpoints accept
 * - fieldsClaimedElsewhere — the fields another column already claims
 * - isMappingDirty — whether the picker has outrun its preview
 * - fieldLabels — canonical field names as the catalog labels them
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * The document formats the dropzone admits, in react-dropzone's accept form.
 *
 * These extensions are the client-side half of the reader's own
 * SUPPORTED_EXTENSIONS: both spreadsheet and CSV documents import, and the
 * dropzone advertises and enforces exactly that pair.
 */
export const GROUP_IMPORT_ACCEPT = Object.freeze({
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
});

/**
 * How many of a preview's warnings the dialog renders in full. A document with
 * a mistyped column can warn on every row; past this many the list stops being
 * something a reader acts on and the remainder is counted instead.
 */
export const WARNING_DISPLAY_LIMIT = 20;

/**
 * The picker value standing for "this column is not imported".
 *
 * A mapping names only the columns it claims, so a column set to this one is
 * absent from the payload rather than present with an empty target.
 */
export const IGNORED_FIELD = '';

/** A column describing the structure itself. */
export const SITE_SCOPE = 'site';

/** A column describing one of a structure's panels. */
export const LOAD_SCOPE = 'load';

// A document reads structure-first, with a structure's panels on the rows
// beneath it, so the picker offers its two groups of fields in that order.
const SCOPE_ORDER = [SITE_SCOPE, LOAD_SCOPE];

const HEADER_WHITESPACE = /\s+/g;
// Trailing decoration on a heading — "Structure Name*", "Normally Closed
// Relay?" — marks the column for a human reader, not a different column.
const HEADER_DECORATION = /[*?:]+$/;

/**
 * Restate a preview's counts in the vocabulary the dialog renders.
 *
 * Counts cover the whole document even though the preview's sample of
 * structures is capped, so these are the numbers the summary quotes. A preview
 * whose summary is absent reads as zeros rather than throwing — a crashed
 * dialog is a dead end, and the warnings alongside it still say what is wrong.
 *
 * @param {?object} preview - a preflight preview, or nothing.
 * @returns {{siteCount: number, newSiteCount: number, existingSiteCount: number,
 *   loadCount: number, skippedRowCount: number, warningCount: number}}
 */
export function summarizeImportPreview(preview) {
  const summary = preview?.summary ?? {};
  return {
    siteCount: summary.site_count ?? 0,
    newSiteCount: summary.new_site_count ?? 0,
    existingSiteCount: summary.existing_site_count ?? 0,
    loadCount: summary.load_count ?? 0,
    skippedRowCount: summary.skipped_row_count ?? 0,
    warningCount: summary.warning_count ?? 0,
  };
}

/**
 * Whether a preview describes an import worth committing.
 *
 * A document can read cleanly and still supply nothing: a row with no usable
 * coordinate is skipped, so a renamed latitude column leaves every row skipped
 * and no structure to create. Such a document must not be submittable — it
 * would queue an import that creates nothing and reports success. Missing
 * required columns are the same refusal caught one step earlier, by name.
 *
 * A preview also describes exactly one mapping. Once the picker holds a
 * different one, the counts on screen belong to a mapping that is no longer the
 * one being submitted, so the preview has to be taken again first — this is the
 * one gate the dialog's submit control consults, not one of several.
 *
 * @param {?object} preview - a preflight preview, or nothing.
 * @param {?object[]} [columns] - the picker model, when there is one to check
 *   the preview against.
 * @returns {boolean} Whether the preview may be submitted.
 */
export function canCommitPreview(preview, columns = null) {
  if (preview == null) {
    return false;
  }
  if ((preview.missing_required ?? []).length > 0) {
    return false;
  }
  if (columns != null && isMappingDirty(columns, preview)) {
    return false;
  }
  return summarizeImportPreview(preview).siteCount > 0;
}

/**
 * Split a preview's warnings into the ones to render and a count of the rest.
 *
 * @param {?object[]} warnings - the warnings a preview carried.
 * @param {number} [limit] - how many to render.
 * @returns {{shown: object[], hiddenCount: number}}
 */
export function selectWarningsToShow(warnings, limit = WARNING_DISPLAY_LIMIT) {
  if (!Array.isArray(warnings) || warnings.length === 0) {
    return { shown: [], hiddenCount: 0 };
  }
  return {
    shown: warnings.slice(0, limit),
    hiddenCount: Math.max(warnings.length - limit, 0),
  };
}

/**
 * A React key for one warning's row.
 *
 * Row, column and code do not identify a warning: two required columns absent
 * at once are reported one per field, all at row 1 with no column, so those
 * three agree and only the field differs. The position in the rendered list is
 * what separates them, and a duplicate key silently drops a sibling.
 *
 * @param {object} warning - one warning from a preview.
 * @param {number} index - the warning's position in the rendered list.
 * @returns {string} A key unique across a preview's warnings.
 */
export function warningRowKey(warning, index) {
  return `${index}-${warning.row}-${warning.column}-${warning.code}`;
}

/**
 * Return the comparison form of a column heading.
 *
 * Case, surrounding space, runs of internal whitespace and trailing reader
 * decoration all vary between documents describing the same column, so none of
 * them distinguish one heading from another. A mapping is keyed by heading, so
 * the dialog has to agree with the reader on when two headings are the same
 * one — this is the reader's own rule, restated on the near side.
 *
 * @param {*} text - a heading cell, whatever type the document held there.
 * @returns {string} The form two headings are compared under.
 */
export function normalizeImportHeader(text) {
  if (text == null) {
    return '';
  }
  const collapsed = String(text).replace(HEADER_WHITESPACE, ' ').trim();
  return collapsed.replace(HEADER_DECORATION, '').trim().toLowerCase();
}

/**
 * Split the field catalog into the groups the picker offers its options under.
 *
 * A document's columns describe two different things — the structure, and one
 * of its panels — and forty flat options give a reader no way to tell which is
 * which. A scope the catalog carries but this dialog does not know is kept and
 * offered last rather than dropped, so a field added upstream stays reachable.
 *
 * @param {?object[]} catalog - the import field catalog.
 * @returns {{scope: string, fields: object[]}[]} Non-empty groups, structure
 *   fields first.
 */
export function groupCatalogByScope(catalog) {
  if (!Array.isArray(catalog)) {
    return [];
  }
  const byScope = new Map();
  catalog.forEach(field => {
    const scope = field?.scope ?? '';
    byScope.set(scope, [...(byScope.get(scope) ?? []), field]);
  });
  const known = SCOPE_ORDER.filter(scope => byScope.has(scope));
  const unknown = [...byScope.keys()].filter(scope => !SCOPE_ORDER.includes(scope));
  return [...known, ...unknown].map(scope => ({ scope, fields: byScope.get(scope) }));
}

/**
 * The set of headings the alias catalog claims on its own.
 *
 * @param {?object[]} catalog - the import field catalog.
 * @returns {Set<string>} Normalized headings the reader resolves unaided.
 */
function aliasClaimedHeadings(catalog) {
  const claimed = new Set();
  if (!Array.isArray(catalog)) {
    return claimed;
  }
  catalog.forEach(field => {
    (field?.aliases ?? []).forEach(alias => claimed.add(normalizeImportHeader(alias)));
  });
  return claimed;
}

/**
 * The picker model for a document's columns, seeded from a preview.
 *
 * Every heading the reader saw appears once: the ones it resolved carry the
 * field it chose, so an auto-resolved document needs no touching, and the ones
 * nothing claimed read as not imported and are ready to be pointed at a field.
 *
 * A heading the alias catalog recognizes by name cannot be turned off. Leaving
 * it out of a mapping only hands the column back to the alias that claimed it,
 * so each column states whether ignoring it is reachable at all rather than
 * offering a choice the reader would overrule in silence.
 *
 * Two columns can carry one heading, which the reader reports as one resolved
 * heading and one it refused. A mapping is keyed by heading, so that pair
 * addresses one column here, not two.
 *
 * @param {?object} preview - a preflight preview, or nothing.
 * @param {?object[]} catalog - the import field catalog.
 * @returns {{heading: string, field: string, canIgnore: boolean}[]} One entry
 *   per addressable column, resolved columns first.
 */
export function buildColumnMappings(preview, catalog) {
  const resolved = preview?.field_map ?? {};
  const unclaimed = Array.isArray(preview?.unmapped_columns) ? preview.unmapped_columns : [];
  const aliasClaimed = aliasClaimedHeadings(catalog);
  const seen = new Set();
  const columns = [];

  const add = (heading, field) => {
    const key = normalizeImportHeader(heading);
    if (key === '' || seen.has(key)) {
      return;
    }
    seen.add(key);
    columns.push({ heading, field, canIgnore: !aliasClaimed.has(key) });
  };

  Object.entries(resolved).forEach(([heading, field]) => add(heading, field));
  unclaimed.forEach(heading => add(heading, IGNORED_FIELD));
  return columns;
}

/**
 * Point one column at a field, or at nothing.
 *
 * @param {?object[]} columns - the picker model.
 * @param {string} heading - the column being retargeted.
 * @param {string} fieldName - a canonical field name, or IGNORED_FIELD.
 * @returns {object[]} A new model; the one handed in is left alone.
 */
export function applyColumnMapping(columns, heading, fieldName) {
  if (!Array.isArray(columns)) {
    return [];
  }
  const key = normalizeImportHeader(heading);
  return columns.map(column =>
    normalizeImportHeader(column.heading) === key ? { ...column, field: fieldName } : column,
  );
}

/**
 * The picker model as the mapping both endpoints accept.
 *
 * Keys are the document's own headings, not column positions: the reader
 * matches a submitted mapping against its header row in normalized form, so a
 * position names no column and the whole mapping is discarded without a word.
 *
 * Every claimed column is stated, including the ones the alias catalog resolved
 * unaided, so the mapping sent is exactly the mapping shown.
 *
 * @param {?object[]} columns - the picker model.
 * @returns {Object<string, string>} Heading → canonical field name, empty when
 *   nothing is claimed.
 */
export function toFieldMapPayload(columns) {
  if (!Array.isArray(columns)) {
    return {};
  }
  return Object.fromEntries(columns.filter(column => column.field).map(column => [column.heading, column.field]));
}

/**
 * The fields some other column already claims.
 *
 * Two columns claiming one field is a mistake the reader settles by keeping the
 * leftmost and reporting the rest, which discards whichever choice the
 * submitter made second. The picker refuses the collision instead of letting it
 * be made and then overruled.
 *
 * @param {?object[]} columns - the picker model.
 * @param {string} heading - the column whose own claim does not count.
 * @returns {Set<string>} Field names unavailable to that column.
 */
export function fieldsClaimedElsewhere(columns, heading) {
  const claimed = new Set();
  if (!Array.isArray(columns)) {
    return claimed;
  }
  const key = normalizeImportHeader(heading);
  columns.forEach(column => {
    if (column.field && normalizeImportHeader(column.heading) !== key) {
      claimed.add(column.field);
    }
  });
  return claimed;
}

/**
 * Whether the picker holds a mapping its preview was not computed under.
 *
 * The counts, warnings and sample a preview carries describe one mapping.
 * Change the mapping and they describe the previous one, so committing on them
 * would import a document nobody previewed.
 *
 * @param {?object[]} columns - the picker model.
 * @param {?object} preview - the preview the model was seeded from.
 * @returns {boolean} Whether the preview has to be taken again.
 */
export function isMappingDirty(columns, preview) {
  const chosen = toFieldMapPayload(columns);
  const resolved = preview?.field_map ?? {};
  const headings = Object.keys(chosen);
  if (headings.length !== Object.keys(resolved).length) {
    return true;
  }
  return headings.some(heading => chosen[heading] !== resolved[heading]);
}

/**
 * Canonical field names as the catalog labels them.
 *
 * The reader names a missing or contested field canonically (`site_name`); a
 * reader of the dialog knows it as the heading it expects ("Structure Name"). A
 * name the catalog does not carry is shown as it arrived rather than dropped,
 * so a field added upstream is still reported.
 *
 * @param {?object[]} catalog - the import field catalog.
 * @param {?string[]} names - canonical field names.
 * @returns {string[]} One label per name, in the order given.
 */
export function fieldLabels(catalog, names) {
  if (!Array.isArray(names)) {
    return [];
  }
  const labels = new Map((Array.isArray(catalog) ? catalog : []).map(field => [field.name, field.label]));
  return names.map(name => labels.get(name) ?? name);
}
